import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { SituationPage } from "@/components/situation-page";
import { faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "Do I need probate before selling a deceased person's house?",
    a: "Usually, yes. If the house was in the deceased's sole name (or as tenants in common), the executor normally needs a grant of probate before the property can be transferred to a buyer. Banks and Titles Queensland will ask for it.",
  },
  {
    q: "What if the house was owned as joint tenants?",
    a: "Then it usually passes automatically to the surviving owner, outside the will. No probate is needed for the house itself; the survivor records the death on the title and can then sell in the ordinary way.",
  },
  {
    q: "Can we sign a contract before probate is granted?",
    a: "It is sometimes possible with the right special conditions, but settlement can't happen until the executor can prove their authority. Signing too early risks a buyer walking away or a settlement date you can't meet. Talk to us before listing.",
  },
  {
    q: "Who signs the contract?",
    a: "The executor (or all executors, if there's more than one), not the beneficiaries. If there's no will, it's the administrator appointed by the court.",
  },
  {
    q: "Should we sell or transfer the house to a beneficiary?",
    a: "Either can work. Transferring to a beneficiary avoids agent fees and may have duty and tax advantages; selling suits estates with several beneficiaries or debts to pay. It depends on the will and the family.",
  },
  {
    q: "What costs come out of the sale?",
    a: "Agent's commission, conveyancing, rates and outgoings to settlement, and the mortgage if any. The balance goes into the estate to be distributed under the will once debts and any claims are dealt with.",
  },
  {
    q: "Can you handle both the probate and the sale?",
    a: "Yes. We do the probate application and the sale conveyancing together, so the timing lines up and you deal with one firm.",
  },
];

export const Route = createFileRoute("/selling-deceased-estate-property-qld")({
  head: () => ({
    meta: [
      { title: fitTitle("Selling a Deceased Estate Property QLD | Coastside Law") },
      {
        name: "description",
        content:
          fitDescription("Selling a house after someone dies in Queensland: when you need probate, joint tenancy, who signs, timing and costs. Probate and sale handled together by one solicitor-led firm."),
      },
      { property: "og:title", content: fitTitle("Selling a deceased estate property in Queensland") },
      {
        property: "og:description",
        content: fitDescription("Probate, who signs and how to time the sale, explained plainly for executors."),
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "/selling-deceased-estate-property-qld" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "/selling-deceased-estate-property-qld" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: () => (
    <SituationPage
      eyebrow="Deceased estates"
      title="Selling a deceased estate property in Queensland"
      lead="If you're an executor and the family home needs to be sold, the order of steps matters. Here's when you need probate, who signs, and how to avoid a sale you can't settle."
      boxTitle="Before you list the house"
      boxItems={[
        "Check how the title was held: sole, joint tenants or tenants in common",
        "Work out whether probate is needed and how long it will take",
        "Only executors (or an administrator) can sign the contract",
        "Line up settlement with the grant, not before it",
        "Insure and secure the property while it's empty",
      ]}
      faqs={FAQS}
      links={[
        { to: "/probate-queensland", label: "Probate in Queensland" },
        { to: "/probate-checklist-queensland", label: "Executor's probate checklist" },
        { to: "/probate-enquiry", label: "Probate enquiry" },
        { to: "/conveyancing", label: "Conveyancing" },
      ]}
      formHeading="Tell us about the estate property"
      ctaTitle="Executor with a house to sell?"
    />
  ),
});
